/**
 * Tap / double-tap detection for scene objects.
 *
 * Single tap → select (via the shared cycle dispatcher, so repeat clicks
 * on the same spot walk through overlapping objects by z-depth).
 * Double-tap → fires an optional callback (e.g. enter pose mode).
 *
 * The low-level gesture work is delegated to `useTapDetection`; this
 * hook layers scene-object selection semantics on top: double-tap,
 * blend-entry monolithic selection, shift/hold additive selection, and
 * cycle dispatch.
 */

import { useCallback, useRef } from "react";
import { findCollectionIdForObject } from "../app/collectionGraph.ts";
import { resolveStoreKeySelectionTarget } from "../app/objectSelection.ts";
import { useStore } from "../app/store.ts";
import { DOUBLE_TAP_DIST_SQ, DOUBLE_TAP_MS } from "./interactionConstants.ts";
import {
  noteNonCycleClick,
  performCycleSelection,
  resetSelectionCycle,
} from "./selectionCycle.ts";
import { useTapDetection } from "./useTapDetection.ts";
import { viewportSupportsSceneInteraction } from "./viewportInteractionPolicy.ts";

export function useObjectTap(
  objectId: string,
  onDoubleTap?: (() => void) | undefined,
  /** When set, single-tap selects the entire blend file entry (monolithic). */
  blendEntryId?: string | undefined,
) {
  useStore((s) => s.selectObject);
  const selectObjectTarget = useStore((s) => s.selectObjectTarget);
  const selectBlendEntry = useStore((s) => s.selectBlendEntry);
  const { handlePointerDown, readTap } = useTapDetection();

  // Per-component double-tap tracking — kept separate from the module-level
  // cycle state so double-click-to-pose and click-through cycling don't
  // stomp on each other's timing windows.
  const lastTapTime = useRef(0);
  const lastTapPos = useRef({ x: 0, y: 0 });

  const handlePointerEnter = useCallback(() => {
    const session = useStore.getState().eyedropperSession;
    if (session && (!session.canPick || session.canPick(objectId))) {
      useStore.getState().setEyedropperHoveredId(objectId);
    }
  }, [objectId]);

  const handlePointerLeave = useCallback(() => {
    const s = useStore.getState();
    if (s.eyedropperSession && s.eyedropperHoveredId === objectId) {
      s.setEyedropperHoveredId(null);
    }
  }, [objectId]);

  const handlePointerUp = useCallback(
    (e: any) => {
      const tap = readTap(e);
      if (!tap) return;
      e.stopPropagation();

      // Eyedropper pick: when a session is active, route the tap directly to the
      // waiting callback instead of going through normal selection/cycling logic.
      const session = useStore.getState().eyedropperSession;
      if (session) {
        if (session.canPick && !session.canPick(objectId)) {
          return;
        }
        session.onPick(objectId);
        useStore.getState().endEyedropperSession();
        return;
      }

      if (
        !viewportSupportsSceneInteraction(useStore.getState().workspaceMode)
      ) {
        return;
      }

      // Double-tap: two quick taps on the same component fire onDoubleTap
      // (e.g. enter pose mode on a mannequin).
      if (onDoubleTap) {
        const now = performance.now();
        const tdx = tap.clientX - lastTapPos.current.x;
        const tdy = tap.clientY - lastTapPos.current.y;
        if (
          now - lastTapTime.current < DOUBLE_TAP_MS &&
          tdx * tdx + tdy * tdy < DOUBLE_TAP_DIST_SQ
        ) {
          lastTapTime.current = 0;
          resetSelectionCycle();
          onDoubleTap();
          return;
        }
        lastTapTime.current = now;
        lastTapPos.current = { x: tap.clientX, y: tap.clientY };
      }

      // Blend entries always select monolithically — the whole group is
      // treated as one unit and cycling isn't meaningful there.
      if (blendEntryId) {
        noteNonCycleClick(tap.clientX, tap.clientY);
        selectBlendEntry(blendEntryId);
        return;
      }

      // Alt+click drills up to the parent collection when the hit object
      // is a member of one — Blender-parity shortcut for "select the
      // group, not the child". Falls back to ordinary selection when the
      // object has no parent collection (or the resolver already promoted
      // the hit, e.g. live-generator collections).
      if (tap.altKey) {
        noteNonCycleClick(tap.clientX, tap.clientY);
        const state = useStore.getState();
        const resolved = resolveStoreKeySelectionTarget(
          state.blendFiles,
          state.collections,
          objectId,
        );
        if (resolved.kind === "sceneObject") {
          const parentCollectionId = findCollectionIdForObject(
            state.collections,
            objectId,
          );
          if (parentCollectionId) {
            selectObjectTarget(
              { kind: "collection", id: parentCollectionId },
              false,
            );
            return;
          }
        }
        selectObjectTarget(resolved, false);
        return;
      }

      // Shift+click (or touch hold-to-add) is additive and should not walk
      // the cycle — it just toggles the frontmost hit.
      if (tap.shiftKey || tap.holdToAdd) {
        noteNonCycleClick(tap.clientX, tap.clientY);
        const state = useStore.getState();
        selectObjectTarget(
          resolveStoreKeySelectionTarget(
            state.blendFiles,
            state.collections,
            objectId,
          ),
          true,
        );
        return;
      }

      // Normal click: route through the cycle dispatcher. It may resolve
      // to this object, an object behind it, or the 3D cursor if one of
      // those is the current cycle advance.
      performCycleSelection(tap.clientX, tap.clientY, tap.intersections, {
        kind: "selection",
        target: resolveStoreKeySelectionTarget(
          useStore.getState().blendFiles,
          useStore.getState().collections,
          objectId,
        ),
      });
    },
    [
      objectId,
      selectObjectTarget,
      selectBlendEntry,
      blendEntryId,
      onDoubleTap,
      readTap,
    ],
  );

  return {
    handlePointerDown,
    handlePointerUp,
    handlePointerEnter,
    handlePointerLeave,
  };
}
